/** @jsx jsx */
import React, { memo, useCallback, useMemo } from 'react';

import { jsx } from '@compiled/react';
import JiraSideNavPlacement from '@post-office-placement/jira-side-nav/placement';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { PostOfficeProvider } from '@atlassian/jira-post-office-provider/src/index.tsx';
import { PlacementFetchProvider } from '@atlassian/jira-post-office-provider/src/placement-fetch-provider.tsx';
import killswitch from '@atlassian/jira-killswitch/src/index.tsx';
import { postOfficeJiraSideNavResource } from '@atlassian/jira-router-resources-post-office/src/services/index.tsx';
import { usePlacementPreloadedData } from '@atlassian/jira-post-office-provider/src/preload-placement.tsx';
import { Placement } from '@atlassian/post-office-placement';
import { useResource } from '@atlassian/react-resource-router';

const PLACEMENT_ID = 'jira-side-nav';

const SideNavPlacement = () => {
	if (__SERVER__ && !fg('jfp-magma-hydration-sidenavrecommendation')) {
		return null;
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const [preloadedInitialData, clearPreloadedInitialData] = usePlacementPreloadedData(
		postOfficeJiraSideNavResource,
	);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const handleClearPreloadedData = useCallback(() => {
		clearPreloadedInitialData();
	}, [clearPreloadedInitialData]);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const memoizedPlacementProps = useMemo(
		() => ({ handleClearPreloadedData, preloadedInitialData }),
		[handleClearPreloadedData, preloadedInitialData],
	);

	return (
		<Placement
			placementId={PLACEMENT_ID}
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore - Type 'ComponentType<Partial<ErrorBoundaryPropsWithRender>> is not assignable to type 'PlacementComponent<LocalPlacementProps>'
			PlacementComponent={JiraSideNavPlacement}
			placementProps={
				fg('post_office_ssr_clear_preloaded_data') ? memoizedPlacementProps : undefined
			}
		/>
	);
};

const SideNavRecommendation = () => {
	if (__SERVER__ && !fg('jfp-magma-hydration-sidenavrecommendation')) {
		return null;
	}

	return (
		<JSErrorBoundary
			id="sideNavRecommendation"
			packageName="side-nav-recommendation"
			teamName="post-office"
			fallback={() => <></>}
		>
			<PostOfficeProvider>
				<div data-testid="side-nav-recommendation.jira-side-nav">
					{fg('post_office_ssr_jira_side_nav_enabled') ? (
						<PlacementFetchProvider resource={postOfficeJiraSideNavResource}>
							<SideNavPlacement />
						</PlacementFetchProvider>
					) : (
						<SideNavPlacement />
					)}
				</div>
			</PostOfficeProvider>
		</JSErrorBoundary>
	);
};

export const Nav4JiraSideNavRecommendation = memo(() => {
	if (__SERVER__ && !fg('jfp-magma-hydration-sidenavrecommendation')) {
		// eslint-disable-next-line jira/ff/no-preconditioning
		return fg('post-office-shadow-rollout-jira-sidenav-ssr') &&
			fg('post-office-jira-side-nav-in-nav4') ? (
			<ServerPlacementAPIFetchEnabled />
		) : null;
	}

	if (!killswitch('killswitch_post_office_in_jira') && fg('post-office-jira-side-nav-in-nav4')) {
		return <SideNavRecommendation />;
	}

	return null;
});

export const JswSideNavRecommendation = memo(() => {
	if (__SERVER__ && !fg('jfp-magma-hydration-sidenavrecommendation')) {
		return fg('post-office-shadow-rollout-jira-sidenav-ssr') ? (
			<ServerPlacementAPIFetchEnabled />
		) : null;
	}

	if (!killswitch('killswitch_post_office_in_jira')) {
		return <SideNavRecommendation />;
	}

	return null;
});

const ServerPlacementAPIFetchEnabled = () => {
	// FIXME: this isn't how useResource works... this can just be removed - comment below preserved for history
	// Use resource to make the shadow API call to Post Office.
	// Don't do anything with the result, we just want to mock the traffic for now.
	useResource(postOfficeJiraSideNavResource);

	return null;
};
